/**
 * Module dependencies.
 */

import { apiEndpoints } from 'app/core/config/api-endpoints';
import { appRoutes } from 'app/routes';
import { omit } from 'lodash';
import {
  CrudAddType,
  CrudListType,
  CrudRemoveType,
  CrudTemplate,
  ListColumns
} from '../../../organisms/crud/form-types';

import { ListFilters } from 'app/components/organisms/crud/form-filters/form-filters-type';
import { TFunction } from 'react-i18next';
import { formFields } from './form-fields';
import { normalizeTemplate } from 'app/components/organisms/crud/utils';
import { DateTime } from 'app/components/atoms/date-time/date-time';
import React from 'react';
import { CCIDrug } from 'app/types/cci';
import { useCrudRequest } from 'app/hooks/app-requests/crud/use-crud-request';
import { IdCopy } from 'app/components/atoms/id-copy/id-copy';

/**
 * List labels.
 */

const listColumns = (translate: TFunction): ListColumns<CCIDrug> => [
  {
    title: translate('common.table.columns.cci'),
    dataIndex: 'cci',
    key: 'cci',
    size: '100px',
    render: item => <IdCopy>{String(item.cci)}</IdCopy>
  },
  {
    title: translate('common.table.columns.drugId'),
    dataIndex: 'drugId',
    key: 'drug.id',
    size: '100px',
    render: item => <IdCopy>{item.drug.id}</IdCopy>
  },
  {
    title: translate('common.table.columns.registry'),
    dataIndex: 'registry',
    key: 'drug.registry',
    size: '100px',
    render: item => <IdCopy>{item.drug.registry}</IdCopy>
  },
  {
    title: translate('common.table.columns.name'),
    dataIndex: 'drugName',
    key: 'drug.productName',
    size: '1fr',
    render: item => JSON.parse(item.drug.productName)?.pt
  },
  {
    title: translate('common.table.columns.description'),
    dataIndex: 'drugDescription',
    key: 'drug.description',
    size: '1fr',
    render: item => JSON.parse(item.drug.productDescription)?.pt
  },
  {
    title: translate('common.table.columns.createdAt'),
    dataIndex: 'createdAt',
    key: 'createdAt',
    size: '200px',
    render: item => <DateTime date={item.createdAt} />
  },
  {
    title: translate('common.table.columns.updatedAt'),
    dataIndex: 'updatedAt',
    key: 'updatedAt',
    size: '200px',
    render: item => <DateTime date={item.updatedAt} />
  }
];

/**
 * List filters.
 */

const listFilters = (translate: TFunction): ListFilters => [
  {
    type: 'inputSearch',
    inline: true,
    name: 'search',
    label: translate('common.labels.search')
  },
  {
    type: 'selectSearch',
    name: 'cci',
    inline: true,
    style: {
      minWidth: 230
    },
    label: translate('common.labels.cci'),
    showSearch: true,
    filterOption: (input, option) => {
      return String(option?.value ?? '')
        .toLowerCase()
        .startsWith(input.toLowerCase());
    },
    allowClear: true,
    options: {
      hook: useCrudRequest as any,
      hookProps: () => {
        return [
          {
            key: ['ccis'],
            endpoint: apiEndpoints.ccisCodes
          }
        ];
      },
      normalize: (items: any) => [
        {
          label: translate('common.labels.empty'),
          value: null
        },
        ...(items?.map((item: any) => ({
          value: item.cci,
          label: item.cci
        })) ?? [])
      ]
    }
  }
];

/**
 * List.
 */

const list = (translate: TFunction): CrudListType<CCIDrug> => ({
  columns: listColumns(translate),
  filters: listFilters(translate),
  enableExport: true,
  resolveParams: (params: any, item: any) => {
    return {
      ...params,
      ...item
    };
  },
  route: appRoutes.dashboard.medical.ccisDrugs,
  addButton: translate('common.actions.add'),
  canEdit: false,
  canRemove: true,
  key: ['ccis'],
  pagination: {
    pageSize: 25,
    pageSizeOptions: [10, 25, 50, 100, 500, 1000]
  },
  endpoint: apiEndpoints.ccisDrugs,
  endpointById: apiEndpoints.ccisDrugsById
});

/**
 * Remove.
 */

const remove = (): CrudRemoveType<CCIDrug> => ({
  endpoint: apiEndpoints.ccisDrugsById
});

/**
 * Normalize payload.
 */

function normalizePayload(values: any) {
  const result = omit(values, ['id', 'createdAt', 'updatedAt']);

  result.cci = parseFloat(result.cci);

  return result;
}

/**
 * Add.
 */

const add = (translate: TFunction): CrudAddType<CCIDrug> => ({
  formFields: formFields(translate),
  normalizePayload
});

/**
 * Config.
 */

export function createCCIsDrugsTemplate(translate: TFunction): CrudTemplate<CCIDrug> {
  return normalizeTemplate(translate, {
    add: add(translate),
    list: list(translate),
    remove: remove()
  });
}
